import * as React from "react";

import type { AudioDockFeatureProps } from "@/components/OmniCine/Docks/audio/audioDock.types";

export default function AudioDockFeature({
  previewStatus,
  previewUrl,
  mediaCurrentTimeSec,
  mediaDurationSec,
  audioProgressPct,
  waveformBars,
  waveMotionPhase,
  mediaIsPlaying,
  stageHeight,
  previewAudioRef,
  fmtTime,
  syncActiveMediaState,
}: AudioDockFeatureProps): JSX.Element {
  return (
    <div
      className="rounded-xl border border-cyan-200/20 bg-[linear-gradient(180deg,rgba(8,28,36,0.55)_0%,rgba(5,10,18,0.72)_100%)] px-3 py-2.5 flex flex-col gap-2"
      style={{ height: stageHeight }}
    >
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider opacity-75">
        <span>Sonic Stage</span>
        <span className="font-mono normal-case tracking-normal">{fmtTime(mediaCurrentTimeSec)} / {fmtTime(mediaDurationSec)}</span>
      </div>

      <div className="relative h-24 rounded-md border border-cyan-300/35 bg-black/60 px-2 py-2 overflow-hidden shadow-[inset_0_0_34px_rgba(0,0,0,0.56),0_0_20px_rgba(34,211,238,0.14)]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_28%,rgba(34,211,238,0.22),transparent_54%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(56,189,248,0.08)_0%,rgba(34,211,238,0.02)_45%,rgba(167,139,250,0.08)_100%)]" />
        <div className="absolute inset-x-0 top-1/2 h-px bg-cyan-200/20" />
        <div
          className="absolute top-2 bottom-2 w-px bg-cyan-200/95"
          style={{
            left: `calc(${audioProgressPct}% - 1px)`,
            boxShadow: "0 0 10px rgba(34,211,238,0.95), 0 0 20px rgba(34,211,238,0.55)",
          }}
        />
        <div className="relative h-full flex items-end gap-[1px]">
          {waveformBars.map((h, i) => {
            const barPct = (i / Math.max(1, waveformBars.length - 1)) * 100;
            const headDistance = Math.abs(barPct - audioProgressPct) / 100;
            const headBoost = Math.max(0, 1 - headDistance * 6.5);

            const waveA = 0.5 + 0.5 * Math.sin(waveMotionPhase * 1.5 + i * 0.45);
            const waveB = 0.5 + 0.5 * Math.sin(waveMotionPhase * 2.6 - i * 0.22);
            const motionBoost = mediaIsPlaying ? 0.26 + 0.42 * waveA + 0.24 * waveB + 0.38 * headBoost : 0;

            return (
              <div
                key={`${i}-${h}`}
                className="min-w-0 flex-1 rounded-sm transition-all duration-100"
                style={{
                  height: `${Math.min(100, Math.max(20, h * (1 + motionBoost)))}%`,
                  background:
                    i % 3 === 0
                      ? "linear-gradient(180deg, rgba(125,211,252,0.96) 0%, rgba(34,211,238,0.82) 60%, rgba(56,189,248,0.56) 100%)"
                      : "linear-gradient(180deg, rgba(103,232,249,0.9) 0%, rgba(34,211,238,0.75) 65%, rgba(56,189,248,0.46) 100%)",
                  opacity: mediaIsPlaying ? Math.min(1, 0.62 + 0.3 * waveA + 0.26 * headBoost) : 0.94,
                  boxShadow: mediaIsPlaying
                    ? `0 0 ${10 + 10 * waveA + 8 * headBoost}px rgba(34,211,238,0.62), 0 0 ${18 + 12 * waveB}px rgba(59,130,246,0.25)`
                    : "0 0 10px rgba(34,211,238,0.62), 0 0 18px rgba(59,130,246,0.25)",
                }}
              />
            );
          })}
        </div>
      </div>

      <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden shadow-[0_0_8px_rgba(34,211,238,0.18)]">
        <div
          className="h-full bg-[linear-gradient(90deg,rgba(34,211,238,0.95)_0%,rgba(56,189,248,0.92)_55%,rgba(125,211,252,0.98)_100%)]"
          style={{
            width: `${audioProgressPct}%`,
            boxShadow: "0 0 10px rgba(34,211,238,0.8), 0 0 20px rgba(56,189,248,0.45)",
          }}
        />
      </div>

      {previewStatus === "ready" && previewUrl && (
        <audio
          ref={previewAudioRef}
          controls={false}
          preload="metadata"
          src={previewUrl}
          className="hidden"
          onLoadedMetadata={syncActiveMediaState}
          onDurationChange={syncActiveMediaState}
          onTimeUpdate={syncActiveMediaState}
          onPlay={syncActiveMediaState}
          onPause={syncActiveMediaState}
          onEnded={syncActiveMediaState}
          onVolumeChange={syncActiveMediaState}
        />
      )}
    </div>
  );
}